import { ChangeDetectionStrategy, signal, inject, Component, input, output, OnChanges, SimpleChanges, Inject, LOCALE_ID } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext'; // Importado
import { SelectModule } from 'primeng/select'; // Importado para p-select
import { DialogModule } from 'primeng/dialog'; // Importado
import { SharedModule } from 'primeng/api'; // Necessário para pTemplate
import { MessageService } from 'primeng/api';

import { AnomaliaMotivoDialogComponent } from './dialogs/anomalia-motivo/anomalia-motivo';

import { Pessoa } from '@/shared/models/pessoa-info.model';
import { BiometriaFeatureService } from './biometria.service';
import { BiometriaRequest, BiometriaResponse } from '@/core/api/sicom-api/registro/medico/biometria/biometria.model';
import { ImageModule } from 'primeng/image';
import { TooltipModule } from 'primeng/tooltip';
import { VisualizadorPdf } from '@/shared/components/visualizador-pdf/visualizador-pdf';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { CardContent } from '@/shared/components/card-content/card-content';
import { BotaoAcoes } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { SwitchSimNao } from '@/shared/components/switch-sim-nao/switch-sim-nao';
import { ESituacaoBiometria, type ResumoDaMao, iconeDaSituacao, resumoDaMao, rotuloDaSituacao, situacaoDaPeca } from './biometria-situacao.presenter';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
// --- Interfaces Auxiliares ---
type DedoNome = 'polegar' | 'indicador' | 'medio' | 'anelar' | 'minimo';
type MaoLado = 'direita' | 'esquerda';

interface MaoState<T> {
    polegar: T;
    indicador: T;
    medio: T;
    anelar: T;
    minimo: T;
}

interface BiometriaDocumento {
    nome: string;
    dataInclusao: string;
    mimeType: string;
    dataUrl: string;
    isNew?: boolean;
    /** Código real do tipo de imagem no SIEM (ex.: DC1, DC2). Necessário para excluir a linha correta. */
    idTipoImagem?: string;
}

interface BiometriaState {
    fotoSrc?: string;
    assinaturaSrc?: string;
    divulgarFoto: boolean;
    dedosSrc: {
        direita: (string | undefined)[];
        esquerda: (string | undefined)[];
    };
    dedosWsqBase64: {
        direita: (string | undefined)[];
        esquerda: (string | undefined)[];
    };
    dedosArquivoOriginalWsq: {
        direita: (boolean | undefined)[];
        esquerda: (boolean | undefined)[];
    };
    motivoAnomalia: {
        maoDireita: MaoState<string>;
        maoEsquerda: MaoState<string>;
    };
    anomalias: {
        foto: boolean;
        assinatura: boolean;
        maoDireita: MaoState<boolean>;
        maoEsquerda: MaoState<boolean>;
    };
    datasImagens: {
        foto: string;
        assinatura: string;
        maoDireita: MaoState<string>;
        maoEsquerda: MaoState<string>;
    };
}

@Component({
    selector: 'app-biometria',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DialogoPadraoDirective,
        SwitchSimNao,
        CommonModule,
        FormsModule,
        ProgressSpinnerModule,
        ToggleSwitchModule,
        CheckboxModule,
        ButtonModule,
        TableModule,
        InputTextModule,
        SelectModule,
        DialogModule,
        SharedModule,
        ImageModule,
        TooltipModule,
        CardContainer,
        CardContent,
        BotaoAcoes,
        AnomaliaMotivoDialogComponent,
        VisualizadorPdf,
        CanDirective
    ],
    templateUrl: './biometria.html',
    styleUrls: ['./biometria.scss']
})
/** Aba de biometria do médico (foto/impressão digital e anomalias). */
export class Biometria implements OnChanges {
    private readonly logger = inject(LoggerService);
    pessoa = input<Pessoa>();
    /** Emitido após salvar a biometria com sucesso, para o container recarregar o card da pessoa (ex.: foto). */
    salvo = output<void>();
    readonly dedosLista = ['polegar', 'indicador', 'medio', 'anelar', 'minimo'] as const;

    /** Vocabulário dos selos, para o template não conhecer os valores do enum. */
    protected readonly ESituacaoBiometria = ESituacaoBiometria;
    protected readonly rotuloDaSituacao = rotuloDaSituacao;
    protected readonly iconeDaSituacao = iconeDaSituacao;

    /** Situação da fotografia — mesma leitura de um dedo: anomalia vence captura. */
    protected situacaoDaFoto(): ESituacaoBiometria {
        return situacaoDaPeca(!!this.state.fotoSrc, this.state.anomalias.foto);
    }

    protected situacaoDaAssinatura(): ESituacaoBiometria {
        return situacaoDaPeca(!!this.state.assinaturaSrc, this.state.anomalias.assinatura);
    }

    protected situacaoDoDedo(lado: MaoLado, indice: number, dedo: DedoNome): ESituacaoBiometria {
        const anomalias = lado === 'direita' ? this.state.anomalias.maoDireita : this.state.anomalias.maoEsquerda;
        return situacaoDaPeca(!!this.state.dedosSrc[lado][indice], anomalias[dedo]);
    }

    /** Contadores do cabeçalho da mão: quantas digitais e quantas ressalvas. */
    protected resumoDeMao(lado: MaoLado): ResumoDaMao {
        const anomalias = lado === 'direita' ? this.state.anomalias.maoDireita : this.state.anomalias.maoEsquerda;
        return resumoDaMao(this.state.dedosSrc[lado], anomalias, this.dedosLista);
    }
    private backupDedo: { src?: string; wsq?: string; isArquivoOriginalWsq?: boolean; data: string } | null = null;

    isLoading = signal(false);
    isEditing = false;
    carregarImagensOpcao = 'local';
    protected readonly roleBiometriaEditar = PERMISSOES.medicos.biometria.editar;
    protected readonly roleBiometriaExcluir = PERMISSOES.medicos.biometria.excluir;

    get acoesFormulario(): AppMenuItem[] {
        return [
            acaoMenuItem({
                id: 'editar',
                label: 'Editar',
                icon: 'pi pi-pencil',
                cor: 'editar',
                disabled: this.isEditing,
                permission: this.roleBiometriaEditar,
                command: () => this.enableEditing()
            })
        ];
    }

    /** Ver acoesFormulario: Salvar e Cancelar saem do menu e viram ação em destaque. */
    get itensEdicao(): AppMenuItem[] {
        const role = this.roleBiometriaEditar;
        return [
            acaoMenuItem({ id: 'salvar', label: 'Salvar', icon: 'pi pi-check', cor: 'salvar', permission: role, command: () => this.saveChanges() }),
            acaoMenuItem({ id: 'cancelar', label: 'Cancelar', icon: 'pi pi-times', cor: 'cancelar', permission: role, command: () => this.cancelEditing() })
        ];
    }

    // Estado principal
    state: BiometriaState = this.getInitialState();

    // Documentos
    documentos: BiometriaDocumento[] = [];
    exibirDialogAnexo = false;
    blobAnexo: Blob | null = null;
    dataUrlAnexo: string | null = null;
    mimeTypeAnexo = 'application/pdf';
    nomeArquivoAnexo = 'anexo.pdf';
    zoomImagemAnexo = 1;

    // Controles para Novo Documento
    showDocumentoDialog = false;
    novoDocumentoNome = '';
    novoDocumentoFile: File | null = null;

    // Opções de descrição para o p-select do diálogo de documentos
    documentoDescricaoOptions = [
        { label: 'Certidão de Nascimento - frente', value: 'Certidão de Nascimento - frente' },
        { label: 'Certidão de Nascimento - verso', value: 'Certidão de Nascimento - verso' },
        { label: 'Certidão de Casamento - frente', value: 'Certidão de Casamento - frente' },
        { label: 'Certidão de Casamento - verso', value: 'Certidão de Casamento - verso' },
        { label: 'CPF', value: 'CPF' },
        { label: 'CNH - aberta', value: 'CNH - aberta' },
        { label: 'CNH - frente', value: 'CNH - frente' },
        { label: 'CNH - verso', value: 'CNH - verso' },
        { label: 'RG - aberta', value: 'RG - aberta' },
        { label: 'RG - frente', value: 'RG - frente' },
        { label: 'RG - verso', value: 'RG - verso' },
        { label: 'Outros', value: 'Outros' }
    ];

    // Snapshots para Rollback
    private editSnapshot: BiometriaState | null = null;
    private documentosSnapshot: BiometriaDocumento[] = [];

    private readonly DEDOS_ORDEM: DedoNome[] = ['polegar', 'indicador', 'medio', 'anelar', 'minimo'];
    IMAGE_STYLE = {
        width: '100%',
        height: '100%',
        maxHeight: '8rem',
        objectFit: 'contain'
    };

    constructor(
        private biometriaService: BiometriaFeatureService,
        @Inject(LOCALE_ID) private locale: string,
        private messageService: MessageService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pessoa']) {
            const p = this.pessoa();
            if (p?.id && p?.uf) {
                this.carregarBiometria(p);
            } else {
                this.resetCompleto();
            }
        }
    }

    // --- Inicialização e Reset ---

    private getInitialState(): BiometriaState {
        const emptyMaoBooleans = () => ({ polegar: false, indicador: false, medio: false, anelar: false, minimo: false });
        const emptyMaoStrings = () => ({ polegar: '', indicador: '', medio: '', anelar: '', minimo: '' });

        return {
            fotoSrc: undefined,
            assinaturaSrc: undefined,
            divulgarFoto: true,
            dedosSrc: {
                direita: [undefined, undefined, undefined, undefined, undefined],
                esquerda: [undefined, undefined, undefined, undefined, undefined]
            },
            dedosWsqBase64: {
                direita: [undefined, undefined, undefined, undefined, undefined],
                esquerda: [undefined, undefined, undefined, undefined, undefined]
            },
            dedosArquivoOriginalWsq: {
                direita: [undefined, undefined, undefined, undefined, undefined],
                esquerda: [undefined, undefined, undefined, undefined, undefined]
            },
            motivoAnomalia: {
                maoDireita: emptyMaoStrings(),
                maoEsquerda: emptyMaoStrings()
            },
            anomalias: {
                foto: false,
                assinatura: false,
                maoDireita: emptyMaoBooleans(),
                maoEsquerda: emptyMaoBooleans()
            },
            datasImagens: {
                foto: '',
                assinatura: '',
                maoDireita: emptyMaoStrings(),
                maoEsquerda: emptyMaoStrings()
            }
        };
    }

    private resetCompleto(): void {
        this.isLoading.set(false);
        this.isEditing = false;
        this.editSnapshot = null;
        this.documentos = [];
        this.documentosSnapshot = [];
        this.state = this.getInitialState();
    }

    // --- Carga de Dados ---

    private carregarBiometria(pessoa: Pessoa): void {
        this.isLoading.set(true);
        this.biometriaService
            .buscar({ id: pessoa.id, uf: pessoa.uf })
            .pipe(
                finalize(() => {
                    this.isLoading.set(false);
                })
            )
            .subscribe({
                next: (lista) => this.processarRespostaApi(lista),
                error: (err) => {
                    // Sem toast local: o interceptor global já exibiu a mensagem do backend. Fica só a
                    // limpeza, para a tela não seguir mostrando a biometria do médico anterior.
                    this.logger.error('Erro ao buscar biometrias:', err);
                    this.resetCompleto();
                }
            });
    }

    get diretorioImagens(): string {
        const id = this.pessoa()?.id || '';
        return `C:\\SIEM\\Cnm 1.5\\IMAGENS\\api\\${id}`;
    }

    copiarDiretorio(): void {
        const path = this.diretorioImagens;
        navigator.clipboard
            .writeText(path)
            .then(() => {
                this.logger.info('Caminho copiado para a área de transferência');
                this.messageService.add({ severity: 'success', summary: 'Copiado', detail: 'Caminho copiado para a área de transferência.' });
            })
            .catch((err) => {
                this.logger.error('Erro ao copiar', err);
                this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível copiar o caminho.' });
            });
    }

    private processarRespostaApi(lista: BiometriaResponse[]): void {
        this.resetCompleto();
        if (!lista?.length) return;

        for (const b of lista) {
            const nomeDoc = (b.nmDocumento || '').trim();
            const tipo = (b.idTipoImagem || '').toUpperCase();

            if (this.isDocumentoComprobatorio(nomeDoc, tipo, b.dsTipoImagem)) {
                this.adicionarDocumento(b, nomeDoc);
                continue;
            }

            this.atualizarBiometriaDoItem(b, tipo);
        }
    }

    private atualizarBiometriaDoItem(b: BiometriaResponse, tipo: string): void {
        const anomalia = (b.inAnomalia || '').toUpperCase() === 'S';
        const img = b.imagemBase64 ? `data:image/png;base64,${b.imagemBase64}` : undefined;
        const dataFmt = this.formatarData(b.dtImagem);

        if (tipo === 'F') {
            this.state.divulgarFoto = (b.inAutorizaDivulgacao || '').toUpperCase() === 'S';
            this.state.anomalias.foto = anomalia;
            this.state.fotoSrc = img;
            this.state.datasImagens.foto = dataFmt;
            return;
        }

        if (tipo === 'A') {
            this.state.anomalias.assinatura = anomalia;
            this.state.assinaturaSrc = img;
            this.state.datasImagens.assinatura = dataFmt;
            return;
        }

        const matchDedo = tipo.match(/^D([1-9]|10)$/);
        if (matchDedo) {
            const num = parseInt(matchDedo[1], 10);
            this.setDedoData(num, img, anomalia, dataFmt, b.isArquivoOriginalWsq);
            const isDireita = num <= 5;
            const index = isDireita ? num - 1 : num - 6;
            const nomeDedo = this.DEDOS_ORDEM[index];
            const motivos = isDireita ? this.state.motivoAnomalia.maoDireita : this.state.motivoAnomalia.maoEsquerda;
            motivos[nomeDedo] = b.txMotivoAnomalia || '';
        }
    }

    private setDedoData(num: number, img: string | undefined, anomalia: boolean, dataFmt: string, isArquivoOriginalWsq?: boolean): void {
        const isDireita = num <= 5;
        const index = isDireita ? num - 1 : num - 6;
        const nomeDedo = this.DEDOS_ORDEM[index];
        const lado: MaoLado = isDireita ? 'direita' : 'esquerda';

        this.state.dedosSrc[lado][index] = img;
        this.state.dedosArquivoOriginalWsq[lado][index] = img ? isArquivoOriginalWsq : undefined;

        const mapAnomalia = isDireita ? this.state.anomalias.maoDireita : this.state.anomalias.maoEsquerda;
        const mapData = isDireita ? this.state.datasImagens.maoDireita : this.state.datasImagens.maoEsquerda;

        mapAnomalia[nomeDedo] = anomalia;
        mapData[nomeDedo] = dataFmt;

        // ADICIONADO: Se não é anomalia (upload novo ou vindo da API como N), limpa o motivo
        if (!anomalia) {
            const mapMotivo = isDireita ? this.state.motivoAnomalia.maoDireita : this.state.motivoAnomalia.maoEsquerda;
            mapMotivo[nomeDedo] = '';
        }
    }

    private setDedoWsqBase64(num: number, base64: string | undefined): void {
        const isDireita = num <= 5;
        const index = isDireita ? num - 1 : num - 6;
        const lado: MaoLado = isDireita ? 'direita' : 'esquerda';
        this.state.dedosWsqBase64[lado][index] = base64;
    }

    rotuloArquivoOriginalWsq(lado: MaoLado, index: number): string {
        return this.state.dedosArquivoOriginalWsq[lado][index] ? 'Digital WSQ' : 'Digital nao WSQ';
    }

    // --- Dialog de Motivo de Anomalia ---
    showMotivoDialog = false;
    private motivoTarget: { lado: MaoLado; index: number; nome: DedoNome } | null = null;

    get motivoDialogValorAtual(): string {
        if (!this.motivoTarget) return '';
        const { lado, nome } = this.motivoTarget;
        const mapa = lado === 'direita' ? this.state.motivoAnomalia.maoDireita : this.state.motivoAnomalia.maoEsquerda;
        return mapa[nome] || '';
    }

    onAnomaliaChanged(lado: MaoLado, nome: DedoNome, index: number, marcado: boolean): void {
        if (marcado) {
            const mapData = lado === 'direita' ? this.state.datasImagens.maoDireita : this.state.datasImagens.maoEsquerda;

            this.backupDedo = {
                src: this.state.dedosSrc[lado][index],
                wsq: this.state.dedosWsqBase64[lado][index],
                isArquivoOriginalWsq: this.state.dedosArquivoOriginalWsq[lado][index],
                data: mapData[nome]
            };

            this.state.dedosSrc[lado][index] = undefined;
            this.state.dedosWsqBase64[lado][index] = undefined;
            this.state.dedosArquivoOriginalWsq[lado][index] = undefined;
            mapData[nome] = '';

            this.motivoTarget = { lado, index, nome };
            this.showMotivoDialog = true;
        } else {
            const mapa = lado === 'direita' ? this.state.motivoAnomalia.maoDireita : this.state.motivoAnomalia.maoEsquerda;
            mapa[nome] = '';
        }
    }

    onMotivoEscolhido(valor: string): void {
        if (!this.motivoTarget) return;
        const { lado, nome } = this.motivoTarget;
        const mapa = lado === 'direita' ? this.state.motivoAnomalia.maoDireita : this.state.motivoAnomalia.maoEsquerda;
        mapa[nome] = valor || '';
        this.motivoTarget = null;
        this.backupDedo = null;
    }

    onMotivoCancelado(): void {
        if (!this.motivoTarget) return;
        const { lado, nome, index } = this.motivoTarget;

        if (lado === 'direita') {
            this.state.anomalias.maoDireita = { ...this.state.anomalias.maoDireita, [nome]: false };
        } else {
            this.state.anomalias.maoEsquerda = { ...this.state.anomalias.maoEsquerda, [nome]: false };
        }

        if (this.backupDedo) {
            const novasImagens = [...this.state.dedosSrc[lado]];
            novasImagens[index] = this.backupDedo.src;
            this.state.dedosSrc[lado] = novasImagens;

            this.state.dedosWsqBase64[lado][index] = this.backupDedo.wsq;
            this.state.dedosArquivoOriginalWsq[lado][index] = this.backupDedo.isArquivoOriginalWsq;

            const mapData = lado === 'direita' ? this.state.datasImagens.maoDireita : this.state.datasImagens.maoEsquerda;
            mapData[nome] = this.backupDedo.data;
        }

        this.motivoTarget = null;
        this.backupDedo = null;
    }

    // --- Documentos ---

    private adicionarDocumento(b: BiometriaResponse, nomeDoc: string): void {
        if (!b.imagemBase64) return;
        const mime = this.inferirMimeType(nomeDoc, b.dsTipoImagem, b.imagemBase64);
        this.documentos.push({
            nome: nomeDoc || 'Desconhecido',
            dataInclusao: this.formatarData(b.dtImagem),
            mimeType: mime,
            dataUrl: `data:${mime};base64,${b.imagemBase64}`,
            isNew: false,
            idTipoImagem: b.idTipoImagem ?? undefined
        });
    }

    private isDocumentoComprobatorio(nome: string, idTipo: string, dsTipo?: string): boolean {
        const n = nome.toUpperCase();
        const t = idTipo.toUpperCase();
        const d = (dsTipo || '').toUpperCase();
        if (n.startsWith('DC') || t === 'DC') return true;
        if (['F', 'A'].includes(t) || /^D([1-9]|10)$/.test(t)) return false;
        return d.includes('DOCUMENTO') || d.includes('COMPROBAT') || !!t;
    }

    visualizarDocumento(doc: BiometriaDocumento): void {
        try {
            const [header, base64] = (doc.dataUrl || '').split(',');
            if (!base64) throw new Error('Conteúdo inválido');
            const mime = doc.mimeType || header.match(/:(.*?);/)?.[1] || 'application/octet-stream';
            const blob = this.base64ToBlob(base64, mime);

            this.mimeTypeAnexo = mime;
            this.nomeArquivoAnexo = doc.nome || 'anexo.pdf';
            this.blobAnexo = mime === 'application/pdf' ? blob : null;
            this.dataUrlAnexo = mime.startsWith('image/') ? doc.dataUrl : null;
            this.zoomImagemAnexo = 1;
            this.exibirDialogAnexo = true;
        } catch (e) {
            this.logger.error('Erro ao abrir documento:', e);
        }
    }

    fecharDialogAnexo(): void {
        this.exibirDialogAnexo = false;
        this.blobAnexo = null;
        this.dataUrlAnexo = null;
        this.mimeTypeAnexo = 'application/pdf';
        this.zoomImagemAnexo = 1;
    }

    diminuirZoomImagemAnexo(): void {
        this.zoomImagemAnexo = Math.max(0.5, Number((this.zoomImagemAnexo - 0.1).toFixed(2)));
    }

    aumentarZoomImagemAnexo(): void {
        this.zoomImagemAnexo = Math.min(2, Number((this.zoomImagemAnexo + 0.1).toFixed(2)));
    }

    downloadAnexoImagem(): void {
        if (!this.dataUrlAnexo) {
            return;
        }

        const anchor = document.createElement('a');
        anchor.href = this.dataUrlAnexo;
        anchor.download = this.nomeArquivoAnexo || 'anexo';
        anchor.click();
    }

    abrirDialogNovoDocumento(): void {
        this.novoDocumentoNome = '';
        this.novoDocumentoFile = null;
        this.showDocumentoDialog = true;
    }

    fecharDialogNovoDocumento(): void {
        this.showDocumentoDialog = false;
        this.novoDocumentoNome = '';
        this.novoDocumentoFile = null;
    }

    onNovoDocumentoFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.novoDocumentoFile = input.files[0];
        }
    }

    adicionarNovoDocumentoLista(): void {
        if (!this.novoDocumentoFile || !this.novoDocumentoNome.trim()) return;

        const file = this.novoDocumentoFile;
        const nome = this.novoDocumentoNome;
        const reader = new FileReader();

        reader.onload = () => {
            const dataUrl = String(reader.result);
            const mime = file.type || 'application/octet-stream';

            this.documentos.push({
                nome: nome,
                dataInclusao: this.formatarData(new Date().toISOString()),
                mimeType: mime,
                dataUrl: dataUrl,
                isNew: true
            });

            this.fecharDialogNovoDocumento();
        };
        reader.readAsDataURL(file);
    }

    removerDocumento(index: number): void {
        this.documentos.splice(index, 1);
    }

    /** Maior número de slot DC<n> já usado nesta sessão (documentos atuais + snapshot de edição). */
    private maiorSlotDocumentoComprobatorio(): number {
        const numeros = [...this.documentos, ...this.documentosSnapshot]
            .filter((d) => !d.isNew && !!d.idTipoImagem)
            .map((d) => Number.parseInt((d.idTipoImagem ?? '').replace(/^DC/i, ''), 10))
            .filter((n) => Number.isFinite(n));
        return numeros.length ? Math.max(...numeros) : 0;
    }

    /** Documentos que existiam no início da edição e foram removidos da lista (exclusões pendentes de gravação). */
    private documentosExcluidos(): BiometriaDocumento[] {
        if (!this.documentosSnapshot?.length) return [];
        const nomesAtuais = new Set(this.documentos.map((d) => d.nome));
        return this.documentosSnapshot.filter((doc) => !!doc?.nome && !nomesAtuais.has(doc.nome));
    }

    /**
     * Exclusão de documento comprobatório pendente de gravação (existia no snapshot e foi removido).
     * Enquanto verdadeiro, o botão de inserir novo documento fica desabilitado — o usuário precisa
     * salvar (ou cancelar) as alterações antes de adicionar outro.
     */
    protected get exclusaoDocumentoPendente(): boolean {
        return this.isEditing && this.documentosExcluidos().length > 0;
    }

    enableEditing(): void {
        this.editSnapshot = structuredClone(this.state);
        // Cria cópia profunda dos documentos
        this.documentosSnapshot = this.documentos.map((d) => ({ ...d }));
        this.isEditing = true;
    }

    saveChanges(): void {
        const p = this.pessoa();
        if (!p) {
            return;
        }
        const toIso = (ddmmyyyy?: string): string | null => {
            if (!ddmmyyyy) return null;
            const parts = ddmmyyyy.split('/');
            if (parts.length !== 3) return null;
            const [dd, mm, yyyy] = parts;
            return `${yyyy}-${mm}-${dd}`;
        };

        const todayIso = new Date().toISOString().split('T')[0];
        const reqs: BiometriaRequest[] = [];

        const prev = this.editSnapshot;

        const prevFoto = prev?.fotoSrc;
        const currFoto = this.state.fotoSrc;
        const prevDivulga = prev?.divulgarFoto;
        const currDivulga = this.state.divulgarFoto;
        if (prevFoto !== currFoto) {
            if (currFoto) {
                const base64Foto = currFoto.split(',')[1] || null;
                reqs.push({
                    medicoId: p.id,
                    uf: p.uf,
                    idTipoImagem: 'F',
                    dtImagem: todayIso,
                    imagemBase64: base64Foto,
                    inAutorizaDivulgacao: currDivulga ? 'S' : 'N'
                });
            } else if (prevFoto) {
                reqs.push({
                    medicoId: p.id,
                    uf: p.uf,
                    idTipoImagem: 'F',
                    dtImagem: null,
                    imagemBase64: null
                });
            }
        } else if (prevDivulga !== currDivulga) {
            // Apenas alteração do marcador de divulgação da Foto
            // Envia atualização incluindo também a imagem e a data correspondente
            if (currFoto || prevFoto) {
                const fotoAtual = currFoto || (prevFoto as string);
                const base64Foto = fotoAtual ? fotoAtual.split(',')[1] || null : null;
                const dtFotoIso = toIso(this.state.datasImagens.foto || '') || todayIso;
                reqs.push({
                    medicoId: p.id,
                    uf: p.uf,
                    idTipoImagem: 'F',
                    dtImagem: dtFotoIso,
                    imagemBase64: base64Foto,
                    inAutorizaDivulgacao: currDivulga ? 'S' : 'N'
                });
            }
        }

        const prevAss = prev?.assinaturaSrc;
        const currAss = this.state.assinaturaSrc;
        if (prevAss !== currAss) {
            if (currAss) {
                const base64Ass = currAss.split(',')[1] || null;
                const dtAssIso = toIso(this.state.datasImagens.assinatura || '') || todayIso;
                reqs.push({
                    medicoId: p.id,
                    uf: p.uf,
                    idTipoImagem: 'A',
                    dtImagem: dtAssIso,
                    imagemBase64: base64Ass
                });
            } else if (prevAss) {
                reqs.push({
                    medicoId: p.id,
                    uf: p.uf,
                    idTipoImagem: 'A',
                    dtImagem: null,
                    imagemBase64: null
                });
            }
        }

        for (let num = 1; num <= 10; num++) {
            const isDireita = num <= 5;
            const index = isDireita ? num - 1 : num - 6;
            const lado: MaoLado = isDireita ? 'direita' : 'esquerda';
            const nomeDedo = this.DEDOS_ORDEM[index];

            const prevSrc = prev?.dedosSrc[lado][index];
            const currSrc = this.state.dedosSrc[lado][index];
            const currWsq = this.state.dedosWsqBase64[lado][index]; // só é preenchido ao inserir/substituir
            const prevAnomalia = isDireita ? (prev?.anomalias.maoDireita[nomeDedo] ?? false) : (prev?.anomalias.maoEsquerda[nomeDedo] ?? false);
            const currAnomalia = isDireita ? this.state.anomalias.maoDireita[nomeDedo] : this.state.anomalias.maoEsquerda[nomeDedo];
            const motivo = isDireita ? this.state.motivoAnomalia.maoDireita[nomeDedo] : this.state.motivoAnomalia.maoEsquerda[nomeDedo];

            if (currWsq) {
                const dtFmt = isDireita ? this.state.datasImagens.maoDireita[nomeDedo] : this.state.datasImagens.maoEsquerda[nomeDedo];
                const dtIso = toIso(dtFmt || '') || todayIso;
                reqs.push({
                    medicoId: p.id,
                    uf: p.uf,
                    idTipoImagem: `D${num}`,
                    dtImagem: dtIso,
                    imagemBase64: currWsq,
                    inAnomalia: currAnomalia ? 'S' : 'N',
                    txMotivoAnomalia: currAnomalia ? motivo || null : null
                });
                continue;
            }

            if (prevSrc && !currSrc) {
                if (prevAnomalia !== currAnomalia || currAnomalia) {
                    // alteração de anomalia (marcada ou desmarcada) sem nova imagem
                    reqs.push({
                        medicoId: p.id,
                        uf: p.uf,
                        idTipoImagem: `D${num}`,
                        dtImagem: todayIso,
                        imagemBase64: null,
                        inAnomalia: currAnomalia ? 'S' : 'N',
                        txMotivoAnomalia: currAnomalia ? motivo || null : null
                    });
                } else {
                    // deleção pura
                    reqs.push({
                        medicoId: p.id,
                        uf: p.uf,
                        idTipoImagem: `D${num}`,
                        dtImagem: null,
                        imagemBase64: null
                    });
                }
                continue;
            }

            // Sem mudança de imagem; apenas toggle de anomalia deve ser enviado
            if (prevAnomalia !== currAnomalia) {
                reqs.push({
                    medicoId: p.id,
                    uf: p.uf,
                    idTipoImagem: `D${num}`,
                    dtImagem: todayIso,
                    imagemBase64: null,
                    inAnomalia: currAnomalia ? 'S' : 'N',
                    txMotivoAnomalia: currAnomalia ? motivo || null : null
                });
            }
        }

        // Próximo slot de documento comprobatório (DC<n>): maior número já usado nesta sessão
        // (documentos atuais + snapshot, incluindo os excluídos) + 1. Evita colidir/sobrescrever um
        // código existente ou recém-excluído quando há lacunas — o legado mantém contíguo reordenando.
        let proximoSlotDc = this.maiorSlotDocumentoComprobatorio();
        this.documentos.forEach((doc) => {
            if (doc.isNew && doc.dataUrl) {
                const base64Doc = doc.dataUrl.split(',')[1];
                proximoSlotDc += 1;
                reqs.push({
                    medicoId: p.id,
                    uf: p.uf,
                    idTipoImagem: `DC${proximoSlotDc}`,
                    dtImagem: todayIso,
                    imagemBase64: base64Doc,
                    nmDocumento: doc.nome
                });
            }
        });

        // Deleções: o que existia no snapshot e não existe mais agora.
        // Usa o código real do tipo de imagem do documento (DC1, DC2...); sem ele não é possível
        // localizar a linha para excluir (enviar 'DC' genérico quebra: não existe em PF_REF_TIPO_IMAGEM).
        this.documentosExcluidos().forEach((prevDoc) => {
            if (!prevDoc.idTipoImagem) return;
            reqs.push({
                medicoId: p.id,
                uf: p.uf,
                idTipoImagem: prevDoc.idTipoImagem,
                dtImagem: null,
                imagemBase64: null,
                nmDocumento: prevDoc.nome
            });
        });

        if (!reqs.length) {
            this.isEditing = false;
            this.editSnapshot = null;
            this.documentosSnapshot = [];
            return;
        }

        this.isLoading.set(true);
        this.biometriaService.salvar(reqs).subscribe({
            next: () => {
                this.isEditing = false;
                this.editSnapshot = null;
                this.documentosSnapshot = [];

                this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Biometrias salvas com sucesso.' });

                // Notifica o container para recarregar o card da pessoa (a foto pode ter mudado).
                this.salvo.emit();

                const p2 = this.pessoa();
                if (p2?.id && p2?.uf) {
                    this.carregarBiometria(p2);
                } else {
                    // fallback improvável
                    this.isLoading.set(false);
                }
            },
            error: (err) => {
                // Sem toast local; o que foi capturado continua na tela para nova tentativa.
                this.logger.error('Falha ao salvar biometrias:', err);
                this.isLoading.set(false);
            }
        });
    }

    cancelEditing(): void {
        if (this.editSnapshot) {
            this.state = this.editSnapshot;
        }
        if (this.documentosSnapshot.length > 0) {
            this.documentos = this.documentosSnapshot.map((d) => ({ ...d }));
        }

        this.isEditing = false;
        this.editSnapshot = null;
        this.documentosSnapshot = [];
        this.fecharDialogNovoDocumento();
    }

    onFileSelected(event: Event, tipoId: string): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        const tipo = tipoId.toUpperCase();
        const hojeFmt = this.formatarData(new Date().toISOString());

        if (/^D([1-9]|10)$/.test(tipo)) {
            if (!file.name.toLowerCase().endsWith('.wsq')) {
                this.logger.warn('Apenas arquivos .wsq permitidos.');
                this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Apenas arquivos .wsq são permitidos para digitais.' });
                input.value = '';
                return;
            }
            const num = parseInt(tipo.substring(1), 10);
            this.isLoading.set(true);
            const readerWsq = new FileReader();
            readerWsq.onload = () => {
                const dataUrl = String(readerWsq.result);
                const base64 = dataUrl.split(',')[1] || '';
                this.setDedoWsqBase64(num, base64);
            };
            readerWsq.readAsDataURL(file);

            this.biometriaService
                .converterWsqParaJpg(file)
                .pipe(
                    finalize(() => {
                        this.isLoading.set(false);
                    })
                )
                .subscribe({
                    next: (blob: Blob) => {
                        const reader = new FileReader();
                        reader.onload = () => {
                            const dataUrl = String(reader.result);
                            this.setDedoData(num, dataUrl, false, hojeFmt, true);
                            input.value = '';
                        };
                        reader.readAsDataURL(blob);
                    },
                    error: (err) => {
                        this.logger.error('Falha ao converter WSQ:', err);
                        this.setDedoData(num, undefined, false, hojeFmt);
                        this.setDedoWsqBase64(num, undefined);
                        input.value = '';
                    }
                });
            return;
        }

        if (!file.type.startsWith('image/')) {
            this.logger.warn('Tipo inválido.');
            input.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const result = String(reader.result);
            if (tipo === 'F') this.state.fotoSrc = result;
            else if (tipo === 'A') {
                this.state.assinaturaSrc = result;
                this.state.datasImagens.assinatura = hojeFmt;
            }
            input.value = '';
        };
        reader.readAsDataURL(file);
    }

    removeImage(tipoId: string): void {
        const tipo = tipoId.toUpperCase();
        if (tipo === 'F') {
            this.state.fotoSrc = undefined;
        } else if (tipo === 'A') {
            this.state.assinaturaSrc = undefined;
            this.state.datasImagens.assinatura = '';
        } else if (/^D([1-9]|10)$/.test(tipo)) {
            const num = parseInt(tipo.substring(1), 10);
            this.setDedoData(num, undefined, false, '');
            this.setDedoWsqBase64(num, undefined);
        }
    }

    onFolderSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) return;

        this.isLoading.set(true);
        const files = Array.from(input.files);

        const filesToProcess: { file: File; type: 'WSQ' | 'FOTO' | 'ASSINATURA'; fingerNum?: number }[] = [];

        files.forEach((file) => {
            const name = file.name.toUpperCase();
            const isImage = file.type.startsWith('image/') || name.endsWith('.JPG') || name.endsWith('.JPEG') || name.endsWith('.PNG');

            if (name.endsWith('.WSQ')) {
                const match = name.match(/(\d+)/);
                if (match) {
                    const num = parseInt(match[1], 10);
                    // Garante que é um dedo válido (1 a 10)
                    if (num >= 1 && num <= 10) {
                        filesToProcess.push({ file, type: 'WSQ', fingerNum: num });
                    }
                }
            } else if (isImage && name.startsWith('F')) {
                filesToProcess.push({ file, type: 'FOTO' });
            } else if (isImage && name.startsWith('A')) {
                filesToProcess.push({ file, type: 'ASSINATURA' });
            }
        });

        if (filesToProcess.length === 0) {
            this.isLoading.set(false);
            this.logger.warn('Nenhum arquivo compatível (WSQ, Foto [F...], Assinatura [A...]) encontrado na pasta.');
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Nenhum arquivo compatível encontrado na pasta selecionada.' });
            return;
        }

        let processedCount = 0;
        const checkDone = () => {
            processedCount++;
            if (processedCount === filesToProcess.length) {
                this.isLoading.set(false);
                input.value = '';
            }
        };

        filesToProcess.forEach((item) => {
            if (item.type === 'WSQ' && item.fingerNum) {
                this.processarArquivoLote(item.file, item.fingerNum, checkDone);
            } else {
                const tipoLetra = item.type === 'FOTO' ? 'F' : 'A';
                this.processarImagemLote(item.file, tipoLetra, checkDone);
            }
        });
    }

    private processarImagemLote(file: File, tipo: 'F' | 'A', onComplete: () => void): void {
        const reader = new FileReader();
        const hojeFmt = this.formatarData(new Date().toISOString());

        reader.onload = () => {
            const result = String(reader.result);

            if (tipo === 'F') {
                this.state.fotoSrc = result;
                this.state.anomalias.foto = false; // Remove flag de anomalia ao carregar imagem
                this.state.datasImagens.foto = hojeFmt;
            } else if (tipo === 'A') {
                this.state.assinaturaSrc = result;
                this.state.datasImagens.assinatura = hojeFmt;
                this.state.anomalias.assinatura = false; // Remove flag de anomalia ao carregar imagem
            }
            onComplete();
        };

        reader.onerror = (err) => {
            this.logger.error(`Erro ao ler arquivo de imagem (${tipo}):`, err);
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: `Falha ao ler arquivo de ${tipo === 'F' ? 'Foto' : 'Assinatura'}.` });
            onComplete(); // Marca como completo mesmo com erro para não travar o spinner
        };

        reader.readAsDataURL(file);
    }

    private processarArquivoLote(file: File, num: number, onComplete: () => void): void {
        const hojeFmt = this.formatarData(new Date().toISOString());

        // 1. Ler WSQ para Base64 (para o objeto de envio/salvar)
        const readerWsq = new FileReader();
        readerWsq.onload = () => {
            const dataUrl = String(readerWsq.result);
            const base64 = dataUrl.split(',')[1] || '';
            this.setDedoWsqBase64(num, base64);
        };
        readerWsq.readAsDataURL(file);

        this.biometriaService.converterWsqParaJpg(file).subscribe({
            next: (blob: Blob) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const dataUrl = String(reader.result);
                    this.setDedoData(num, dataUrl, false, hojeFmt, true);
                    onComplete();
                };
                reader.readAsDataURL(blob);
            },
            error: (err) => {
                // Sem toast local. Este roda em laço por dedo: um toast por falha empilharia vários
                // avisos da mesma operação. O `onComplete` mantém o laço andando.
                this.logger.error(`Falha ao converter WSQ do dedo ${num}:`, err);
                onComplete();
            }
        });
    }

    private formatarData(valor?: string | null): string {
        if (!valor) return '';
        try {
            if (typeof valor === 'string' && valor.match(/^\d{4}-\d{2}-\d{2}/)) {
                const [y, m, d] = valor.split('T')[0].split('-');
                return `${d}/${m}/${y}`;
            }
            return formatDate(valor, 'dd/MM/yyyy', this.locale);
        } catch {
            return String(valor);
        }
    }

    private inferirMimeType(nome: string = '', dsTipo: string = '', base64: string = ''): string {
        const str = (nome + dsTipo).toLowerCase();
        if (str.includes('pdf') || base64.startsWith('JVBERi0')) return 'application/pdf';
        if (str.includes('png') || base64.startsWith('iVBORw0')) return 'image/png';
        if (str.match(/jpe?g/) || base64.startsWith('/9j/')) return 'image/jpeg';
        return 'application/octet-stream';
    }

    private base64ToBlob(base64: string, mime: string): Blob {
        const byteChars = atob(base64);
        const byteArrays = [];
        for (let i = 0; i < byteChars.length; i += 512) {
            const slice = byteChars.slice(i, i + 512);
            const byteNumbers = new Array(slice.length);
            for (let j = 0; j < slice.length; j++) byteNumbers[j] = slice.charCodeAt(j);
            byteArrays.push(new Uint8Array(byteNumbers));
        }
        return new Blob(byteArrays, { type: mime });
    }
}
